import { useCallback, useEffect, useRef, useState } from "react";

export type ToneKind = "tick" | "chime" | "thud" | "ping" | "sweep" | "welcome";

const STORAGE_KEY = "ap-soundscape-enabled";

let sharedCtx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
  if (!Ctx) return null;
  if (!sharedCtx) sharedCtx = new Ctx();
  if (sharedCtx.state === "suspended") sharedCtx.resume().catch(() => {});
  return sharedCtx;
}

interface ToneSpec {
  type: OscillatorType;
  freq: number;
  freq2?: number;
  duration: number;
  vol: number;
}

const TONES: Record<ToneKind, ToneSpec> = {
  tick:    { type: "sine",     freq: 1400, duration: 0.012, vol: 0.04 },
  chime:   { type: "triangle", freq: 880,  freq2: 1320, duration: 0.5,  vol: 0.08 },
  thud:    { type: "sine",     freq: 120,  freq2: 60,   duration: 0.18, vol: 0.12 },
  ping:    { type: "sine",     freq: 1760, freq2: 2200, duration: 0.32, vol: 0.06 },
  sweep:   { type: "sine",     freq: 320,  freq2: 720,  duration: 0.22, vol: 0.05 },
  welcome: { type: "triangle", freq: 523,  freq2: 784,  duration: 0.7,  vol: 0.07 },
};

function playTone(kind: ToneKind) {
  const ctx = getCtx();
  if (!ctx) return;
  const spec = TONES[kind];
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = spec.type;
  osc.frequency.setValueAtTime(spec.freq, ctx.currentTime);
  if (spec.freq2) {
    osc.frequency.exponentialRampToValueAtTime(spec.freq2, ctx.currentTime + spec.duration);
  }
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(spec.vol, ctx.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + spec.duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + spec.duration + 0.02);
}

export function useSoundscape() {
  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY) === "1";
  });
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const toggle = useCallback(() => {
    setEnabled((v) => {
      const next = !v;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch {}
      if (next) playTone("welcome");
      return next;
    });
  }, []);

  const play = useCallback((kind: ToneKind) => {
    if (!enabledRef.current) return;
    try { playTone(kind); } catch {}
  }, []);

  return { enabled, toggle, play };
}

// Lightweight imperative API for places where hooks aren't ideal
export function playToneIfEnabled(kind: ToneKind) {
  if (typeof window === "undefined") return;
  if (localStorage.getItem(STORAGE_KEY) !== "1") return;
  try { playTone(kind); } catch {}
}
